// TODO check invite_team_token

import { DebouncedFunc } from "lodash";
import { Id } from "react-toastify";
import { DESIGN_CHANGE_ENUM } from "../utils/ReactHooks";
import { ROLES_ENUM } from "../service/authService";
import { HISTORY_EVENT, PROJECT_TYPES } from "../constant/common.constant";

// API response shapes — mirrors backend createSuccessResponse / createErrorResponse
export type ApiSuccessResponse<T = unknown> = { data: T };
export type ApiErrorResponse = {
  error: { message: string; code?: number; details?: unknown };
};

//Common

export type Currency = "cad" | "usd";
export type Language = "fr" | "en";
export type Country = "CA" | "US";
export type OrderType = "preorder" | "order";
export type ShippingTier = "standard" | "express";
export type FactoryId = "RI" | "ICX" | "AT" | "OW";

export enum CURRENCY {
  CAD = "cad",
  USD = "usd",
}

export enum COUNTRY {
  CA = "CA",
  US = "US",
}

export enum LANGUAGE {
  FR = "fr",
  EN = "en",
}

export type PropsWithDataTest<T = unknown> = T & {
  "data-test"?: string;
};

export type Response<T> = {
  data: T;
  message: string;
  status: string;
};

export type SuccessPaginatedResponseMessage<T> = {
  status: string;
  message: {
    count: number;
  } & T;
};

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface UserOnboardingInfo {
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  country?: Country;
  optNewsletter?: boolean;
  language?: string;
  forWho?: PROJECT_TYPES;
  projectSize?: string;
  teamName?: string;
  eventDate?: string;
  campaign?: string;
  inviteToken?: string;
  contactPhone?: string;
  projectName?: string;
  organizationName?: string;
}

export interface Project {
  coverUuid?: string;
  cover_uuid?: string;
  createdAt: Date;
  deletedAt?: Date;
  deleted_at?: Date;
  iconUuid?: string;
  icon_uuid?: string;
  id: string;
  marketingEstimatedProjectSize: string;
  marketingEventDate?: Date;
  marketingForWho: string;
  marketing_estimated_project_size: string;
  marketing_event_date?: Date;
  marketing_for_who: string;
  name: string;
  shareableToken: string;
  shareable_token: string;
  teamId: string;
  team_id: string;
  updatedAt: Date;
  updated_at: Date;
  userId?: string;
  user_id?: string;
}

export interface ProjectWithDesigns extends Project {
  designs: DesignState[];
}

export interface Role {
  role: ROLES_ENUM;
}

export interface Product {
  family: DesignFamily;
  product: DesignProduct;
  labelTag: string;
  imgName: string;
  minQty: number;
  comingSoon?: boolean;
  line: ProductLineType;
  adminOnly?: boolean;
  deprecated?: boolean;
  sizeChartUrl?: { fr: string; en: string };
}

export interface ProductFamily {
  id: string;
  labelTag: string;
  imgName: string;
  comingSoon?: boolean;
  lines: { line: ProductLineType; adminOnly?: boolean }[];
}
//use at the end, when the structure is defined
// export enum ProductFamilyType {
//   TSHIRT = "tshirt",
//   LONGSLEEVE = "longsleeve",
//   POLO = "polo",
//   SWEATSHIRT = "sweatshirt",
//   HOODIE = "hoodie",
//   POLAR = "polar",
//   MOCKNECK = "mockneck",
//   SLEEVELESS = "sleeveless",
//   SWEATPANTS = "sweatpants",
//   BEANIE = "beanie",
//   SOCKS = "socks",
// }

export enum ProductLineType {
  ECO = "ECO",
  HYPER_CUSTOM = "HYPER_CUSTOM",
  CLASSIC = "CLASSIC",
}

export interface Team {
  createdAt: Date;
  deleted_at?: Date;
  id: string;
  name: string;
  teammates: Teammate[];
  updated_at: Date;
  isTemplate?: boolean;
  isSourceTemplate?: boolean;
}

export interface TeamWithProjects extends Team {
  projects: Project[];
}

export interface TeamWithProjectsAndDesigns extends Team {
  projects: ProjectWithDesigns[];
}

export interface Teammate {
  email: string;
  firstName: string;
  first_name: string;
  id: string;
  lastName: string;
  last_name: string;
  isInternalTeamMember: boolean;
  imageUrl?: string;
}

export interface FlattenTeam {
  teamId: string;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  userId: string;
}

export interface UserInfo {
  country: Country;
  currency: Currency;
  canAddArtwork: boolean;
  canAddDesign: boolean;
  canAddProject: boolean;
  canAddTeam: boolean;
  canDeleteArtwork: boolean;
  canDeleteDesign: boolean;
  canDeleteProject: boolean;
  canDeleteTeam: boolean;
  canMoveDesign: boolean;
  canRenameDesign: boolean;
  canRenameProject: boolean;
  canRenameTeam: boolean;
  canUpdateArtworkColor: boolean;
  canUpdateArtworkPosition: boolean;
  canUpdateDesignColors: boolean;
  canUpdateDesignStyles: boolean;
  email: string;
  exp: number;
  firstName: string;
  iat: number;
  id: string;
  isAdmin: boolean;
  isDesigner: boolean;
  isUser: boolean;
  lastName: string;
  optNewsletter: boolean;
  imageUrl: string;
  roles: Role[];
  forWho: string;
}

export interface ManagedUserRole {
  role: ROLES_ENUM;
}

export interface ManagedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: ROLES_ENUM;
  roles: ManagedUserRole[];
  priceTier: "A1" | "A2" | "A3" | "C" | null;
  createdAt: string;
}

export enum AddressTypeEnum {
  SHIPPING = "shipping",
  BILLING = "billing",
}

export interface ClientDetails {
  clientName: string;
  clientNumber: string;
  clientProjectName: string;
}

//to backend
export interface AddressFormType {
  id?: string;
  organization: string;
  firstName: string;
  lastName: string;
  address: string;
  appartment: string;
  city: string;
  country: Country;
  state: string;
  zipcode: string;
  phone: string;
  // phoneExt: string;
  email: string;
  communicationLanguage?: Language;
  ein?: string;
  type: AddressTypeEnum;
}

//from backend
export interface AddressType {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organization: string;
  phone: string;
  phoneExt?: string;
  address: {
    city: string;
    country: Country;
    line1: string;
    line2: string;
    zipcode: string;
    state: string;
  };
  type: AddressTypeEnum;
  communicationLanguage?: Language;
  ein?: string;
}

export interface Size {
  name: string;
  displayLabel?: string;
  extraSize?: boolean;
  qty?: number;
}

export interface Color {
  name: string;
  hex: string;
  label: string;
  disabled: boolean;
}

export interface Image {
  url: string;
  side: string;
  imageS3Bucket?: string;
  path?: string;
}

export interface FileType {
  extensions: string[];
  name: string;
}

export interface DeliveryTime {
  minWeeks: number;
  maxWeeks: number;
}

export interface DesignFamily {
  name: string;
  version: string;
}

export interface DesignProduct {
  name: string;
  version: string;
  specRevisionId?: string;
}

export interface DisplayName {
  fr: string;
  en: string;
}

export enum GLASS_MAGNIFIER_BACKGROUND_COLORS {
  LIGHT = "#DADADA",
  DARK = "#666666",
  LIGHT_GREY = "#F1F1F1",
  WHITE = "#FFFFFF",
}

///////////////////////// Specs
export interface PricingRange {
  qty: number;
  price: number;
  shipping: number;
}

export interface PricePerSize {
  sizeName: string;
  price: number;
  qtyThreshold: number;
  min: number;
  max: number;
}

export interface CollectSizeOrder {
  createdAt: Date;
  deletedAt?: Date;
  designId: string;
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  quantity: number;
  size: string;
  updatedAt: Date;
}

export interface CollectSizeOrderGrouped {
  email: string;
  first_name: string;
  last_name: string;
  designId: string;
  sizes: {
    sizeName: string;
    quantity: number;
  }[];
  createdAt: Date;
}

export interface Specs {
  families: SpecsFamily[];
  products: SpecsProduct[];
  fabrics: SpecsFabric[];
  decorations: SpecsDecoration[]; // TODO valid if its still needed
}

export interface SpecsFamily {
  name: string;
}

export interface SpecsProduct {
  name: string;
  displayName: string;
  versions: SpecsProductVersion[];
}

export interface SpecsFabric {
  name: string;
  defaultColor: Color;
  colors: Color[];
}

export interface SpecsDecoration {
  id: string;
  name: string;
}

export interface SpecsProductVersion {
  name: string;
  deliveryTime: DeliveryTime;
  sizes: Size[];
  pricing: SpecsProductPricing;
  productLine: ProductLineType;
}

export interface SpecsProductSpecific {
  product: SpecsProductVersion;
  fabrics: SpecsFabric[];
  decorations: SpecsDecoration[];
  sizes: Size[];
  pricing: SpecsProductPricing;
}

export interface SpecsProductPricing {
  freeCredits: number;
  minQty: number;
  pricingCurrency: SpecsProductPricingCurrency;
}

export type SpecsProductPricingCurrency = {
  [key in Currency]: {
    carbonNeutralityFee: number;
    priceOfExtraCredit: number;
    pricingRange: PricingRange[];
    pricePerSize: PricePerSize[];
  };
};

// Order

export interface Invoice {
  chargeId: string;
  checkoutSessionId: string;
  currency: Currency;
  customer: AddressType;
  discount: number;
  discounts: Discount[];
  hasReachedMaximum: boolean;
  invoiceNumber?: string;
  invoice_number?: string;
  locale: Language;
  paymentIntentId: string;
  receiptUrl: string;
  shipping: number;
  subtotal: number;
  tax: number;
  taxes: Tax[];
  total: number;
}

export interface Discount {
  id: string;
  amount: number;
  name: string;
}

export interface Tax {
  id: string;
  amount: number;
  jurisdiction: string;
  name: string;
  percentage: number;
}

export interface EventHistory {
  name: HISTORY_EVENT;
  data: EventHistoryData;
}

export interface EventHistoryData {
  new?: EventHistoryActionData;
  old?: EventHistoryActionData;
}

export interface EventHistoryActionData {
  action: string;
  data: any;
  currency?: Currency;
  // {
  //   color: string;
  //   decorationId: string;
  //   index: number;
  //   transparent: boolean;
  //   discounts: number;
  //   currency: Currency;
  //   shipping: AddressType;
  //   purchasedDesignId: string;
  //   designId: string;
  //   sizes: Size[];
  //   status: string;
  //   externalInvoiceString: string;
  //   billing: AddressType;
  //   purchasedItemId: string;
  //   purchasedItem: CartItem;
  //   preorder: PreOrder;
  // };
}

export interface History {
  User: User;
  UserId: string;
  createdAt: Date;
  deleted_at?: Date;
  event: EventHistory;
  id: string;
  preorder_id: string;
  updated_at: Date;
  user_id: string;
}

export interface PreOrder {
  status:
    | "intent"
    | "purchased"
    | "cancelled"
    | "inreviewbycustomer"
    | "inreviewbydesigner"
    | "shipped"
    | "delivered"
    | "inproduction";
  createdAt: Date;
  id: string;
}

export interface OrderDetail {
  billingAddress: AddressType;
  history: History[];
  /// a valider
  invoiceNumber?: string;
  invoice: Invoice;
  isDistributor?: boolean;
  poNumber?: string;
  notes?: string;
  clientDetails?: ClientDetails;
  paymentOption?: PreOrderPaymentOptions;
  preorder?: PreOrder;
  pricing: Cart;
  /// Not used for now
  promoCode: string;
  shippingAddress: AddressType;
  techpack: {
    id: string;
    projectNumber: string;
  };
  user: User;
  showDistributorPrices: boolean;
}

export enum PreOrderPaymentOptions {
  BANK_TRANSFER = "BANK_TRANSFER",
  CREDIT_CARD = "CREDIT_CARD",
  INTERAC_E_TRANSFER = "INTERAC_E_TRANSFER",
  CHEQUE = "CHEQUE",
  NOT_SET = "NOT_SET",
}

export interface PurchasedItem {
  deprecatedDesign: boolean;
  designId: string;
  designName: string;
  id: string;
  original_design_id: string;
  pricing: CartItemPricing;
  productImages: Image[];
  productName: string;
  projectId: string;
  projectName: string;
  sizes: Size[];
  teamId: string;
  teamName: string;
  versionName: string;
  designState: {
    // TODO TO remove once the backend is give me creditsUsedInDesign in Cart.Pricing.creditsUsedInDesign
    customizationPricing: DesignPricing;
  };
}

export interface Techpack {
  id: string;
  orderType: OrderType;
  orderId: string;
  projectNumber?: string;
  customerNumber?: string;
  generatedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  techpackitems: TechpackItem[];
}

export interface TechpackItem {
  id: string;
  techpackId: string;
  displayOrder: number;
  purchasedDesignId: string;
  purchasedItem?: any;
  version: string;
  sku: string;
  putSkuOnProduct: boolean;
  cardboardSleeves: boolean;
  individualPolyBags: boolean;
  customNeckTags: boolean;
  priceOption?: PriceOption;
  hangTags: boolean;
  customPrice?: number;
  differentQuantity?: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  TechpackId: string;
  factory: string;
  factoryInfo: string;
  factoryPosition: number;
  totalInFactory: number;
  techpackitemquantities: TechpackItemQuantity[];
  techpackitemartworks: TechpackItemArtwork[];
  techpackitemcustomcolors: TechpackItemCustomColor[];
  purchaseddesign: PurchasedDesign;
  designState: TechpackDesignState;
  decorationDefinitions?: ProductDefinitionDecoration[];
}

export enum PriceOption {
  REGULAR_PRICE = "regular_price",
  FREE_DEFECT_REPLACEMENT = "free_defect_replacement",
  CUSTOM_PRICE = "custom_price",
  PRE_PRODUCTION_SAMPLE = "pre_production_sample",
  DIFFERENT_QTY = "different_qty",
}

export interface TechpackItemQuantity {
  id: string;
  techpackItemId: string;
  sizeName: string;
  displayOrder: number;
  originalOrderQuantity: number;
  orderQuantity: number;
  overQuantity: number;
  qcQuantity: number;
  marketingQuantity: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  TechpackItemId: string;
}

export interface TechpackItemArtwork {
  // Identifiers
  id: string;
  techpackItemId: string;

  // Ordering & type
  displayOrder: number;
  decorationType: string;

  // Decoration details
  decoration: DesignDecoration & {
    asset?: DecorationImage;
    fabricColorCodes?: AssetColor[];
    assetOriginal?: DecorationImage;
    assetGeneratedTag?: DecorationImage;
    embroideryPatchDetails?: DesignDecorationTagDetails;
    area?: number;
  };

  // Production / preview / original versions
  productionDecoration?: any;
  productionDecorationBackground?: { color: string };
  originalDecorationBackground?: { color: string };
  previewDecorationBackground?: { color: string };

  // Custom colors
  techpackitemartworkcustomcolors?: TechpackItemCustomColor[];

  // Dates
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt?: Date | string | null;
}

export interface TechpackItemCustomColor {
  id: string;
  techpackItemId: string;
  techpackItemArtworkId: string | null;
  fabricHexKey: string;
  hexColor: string;
  fabric: {
    fabricName: string;
    fabricDescription: string;
  };
  customColorName: string | null;
  usedOn: {
    partName: string;
    elementName: string;
    styleName: string;
    layerName: string;
    techpackPosition: string;
    decoration: string;
  }[];
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface PurchasedDesign {
  id: string;
  userId: string;
  orderType: OrderType;
  orderId: string;
  originalDesignId: string;
  originalProjectId: string;
  originalTeamId: string;
  originalDesignName: string;
  originalProjectName: string;
  sizes: Size[];
  shippingTier?: ShippingTier;
  factoryId: FactoryId;
  carbonNeutral: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  UserId: string;
}

export interface TechpackDesignState extends DesignFromGenerator {
  customizationPricing: DesignPricing;
  deliveryTime: DeliveryTime;
  design: Design;
  designId: string;
  designWithColors: DesignWithColors;
  family: DesignFamily;
  imageUUID: string;
  images: Image[];
  latestImage: number;
  latestUpdate: number;
  ownerId: string;
  product: DesignProduct;
  productLine: ProductLineType;
  shipping: ShippingOption[];
  siblingProducts: DesignStateSiblingProduct[];
  version: number;
}

export interface AdminPurchasedOrderType {
  createdAt?: Date;
  details: OrderDetail;
  type: OrderType;
}

//Pricing

export interface DesignPricing {
  creditsUsedInDesign: number;
  customizationsListWithCredits: DesignPricingCustomizationWithCredits[];
}

export interface DesignPricingCustomizationWithCredits {
  credits: number;
  displayLabels: string[];
  explanation: string;
  fabric: string;
  id: string;
  name: string;
  type: string;
}

// Design State

export interface DesignFromGenerator {
  customizationPricing: DesignPricing;
  deliveryTime: DeliveryTime;
  design: Design;
  designId: string;
  designWithColors: DesignWithColors;
  family: DesignFamily;
  imageUUID: string;
  images: Image[];
  latestImage: number;
  latestUpdate: number;
  product: DesignProduct;
  siblingProducts: DesignStateSiblingProduct[];
  specRevisionId?: string;
  version: number;
  errorKey?: string;
  productLine?: ProductLineType;
}

export type DesignState = Partial<Omit<DesignFromGenerator, "images">> &
  DesignFromDB & { images: DesignFromGenerator["images"] };

export interface DesignStateSiblingProduct {
  icon: string;
  product?: DesignProduct;
  name: string;
  version: string;
}

export interface DesignFromDB {
  collectableSince?: string;
  collectable_since?: string;
  createdAt: Date;
  deletedAt?: Date;
  deleted_at?: Date;
  description?: string;
  id: string;
  name: string;
  project?: Project;
  projectId: string;
  project_id: string;
  updatedAt: Date;
  updated_at: Date;
  isFavorite?: boolean;
  locked?: boolean;
  lockedVersion?: string;
}

export interface Design {
  containsColorsLikelyToBleedOnEachOther: boolean;
  containsColorsThatTendsToBeBledOn: boolean;
  containsColorsThatTendsToBleed: boolean;
  containsColorsEndOfLife: boolean;
  decorations: DesignDecoration[];
  deprecated: boolean;
  family: DesignFamily;
  needsMigration: boolean;
  parts: DesignPart[];
  product: DesignProduct;
}

export interface DesignDecoration {
  active: boolean;
  assetId: string;
  assetSize: DesignDecorationAssetSize;
  assetVersionId: string;
  deprecated: boolean;
  group: string;
  id: string;
  inError: boolean;
  inWarning: boolean;
  isVectorImage: boolean;
  latestUpdate: number;
  listOrder: number;
  location: DesignDecorationLocation;
  message: string;
  name: string;
  icon: string;
  originalHeight: number;
  originalTrimmedHeight: number;
  originalTrimmedWidth: number;
  originalWidth: number;
  previewBackgroundColor: GLASS_MAGNIFIER_BACKGROUND_COLORS;
  priority: number;
  ratio: string;
  repeatingDetails: DesignDecorationRepeatingDetails;
  tagDetails: DesignDecorationTagDetails;
  type: string;
  warningMessages: string[];
  errorFlag: string;
}

export interface DesignDecorationAssetSize {
  availableSizes: DesignDecorationAssetSizeSize[];
  inWarning: boolean;
  selectedSize: DesignDecorationAssetSizeSize;
  warningMessages: string[];
}

export interface DesignDecorationAssetSizeSize {
  drivingSide: string;
  heightInInches: number;
  id: number;
  scale: number;
  widthInInches: number;
}

export interface DesignDecorationLocation {
  availableLocations: DesignDecorationLocationType[];
  selectedLocation: DesignDecorationLocationType;
}

export interface DesignDecorationLocationType {
  allowedDecorationTypes: string[];
  coverArea: boolean;
  designTemplates: DesignDecorationLocationDesignTemplate[];
  dropdownLabel: string;
  elementName: string;
  groupName: string;
  imageTooSmall: boolean;
  inUse: boolean;
  increment: number;
  maxHeightInches: number;
  maxWidthInches: number;
  minHeightInches: number;
  minTagHeightInches: number;
  minTagWidthInches: number;
  minWidthInches: number;
  partName: string;
  planeAngle: number;
  positionId: string;
  positionName: string;
  spotTaken: boolean;
  styleName: string;
}

export interface DesignDecorationLocationDesignTemplate {
  name: string;
  url: string;
  // {
  //     name: "canvasTemplate",
  //     url: "" // TODO: Canvas link
  // },
}

export interface DesignDecorationRepeatingDetails {
  backgroundColorName?: string;
  horizontalOffsetInches: number;
  horizontalSpacingInches: number;
  maxImageHeightInches: number;
  maxImageWidthInches: number;
  patternName: string;
  verticalOffsetInches: number;
  verticalSpacingInches: number;
}

export interface DesignDecorationTagDetails {
  availableTagNames: string[];
  backgroundColorName: string;
  backgroundFabricName: string;
  stitchColorName: string;
  stitchFabricName: string;
  tagName: string;
  textureName: string;
  logoColorNames: string[];
}

export interface DesignPart {
  elements: DesignPartElement[];
  inheritFrom: string;
  mainElement: string;
  name: string;
}

export interface DesignPartElement {
  active: boolean;
  enabled: boolean;
  name: string;
  reasonForBeingDisabled: DesignPartElementDisabledReason[];
  style: DesignPartElementStyle;
}

export interface DesignPartElementDisabledReason {
  msg: string;
  disabledByPart: string;
  disabledByElement: string;
  disabledByStyle: string;
}

export interface DesignPartElementStyle {
  layers: DesignPartElementStyleLayer[];
  name: string;
  deprecated?: boolean;
  selectedColorCombo?: string; ///FIXME - find the right type, checker polo
}

export interface DesignPartElementStyleLayer {
  active: boolean;
  colorName: string;
  techpackColorName: string;
  colorLabel: string;
  hex: string;
  containsColorsThatTendsToBeBledOn?: boolean;
  containsColorsThatTendsToBleed?: boolean;
  containsColorsEndOfLife?: boolean;
  credits: number;
  deprecated: boolean;
  name: string;
  type: string;
}

export interface DesignWithColors {
  decorations: DesignDecoration[];
  family: DesignFamily;
  parts: DesignPart[];
  product: DesignProduct;
}

// Product Definition

export interface ProductDefinition {
  availableDecorationTypes: { name: string }[];
  availableImageSides: { name: string }[];
  decorations: ProductDefinitionDecoration[];
  deliveryTime: DeliveryTime;
  fabrics: ProductDefinitionFabric[];
  factory: string;
  generator: ProductDefinitionGenerator;
  labels: ProductDefinitionLabel[];
  name: string;
  parts: ProductDefinitionPart[];
  positionGroups: ProductDefinitionPositionGroup[];
  positions: ProductDefinitionPosition[];
  pricing: SpecsProductPricing;
  productSex: string;
  sizes: Size[];
  status: "active" | "deprecated";
  shipping: ShippingOption[];
}

export interface ProductDefinitionDecorationGenerator {
  effect?: string;
  fabricName?: string;
  effectDetails?: Record<string, unknown>;
}

export interface ProductDefinitionDecoration {
  adminOnly: boolean;
  distributorOnly: boolean;
  behavesLike: string;
  display: ProductDefinitionDecorationDisplay;
  displayName: DisplayName;
  fileTypes: FileType[];
  generator?: ProductDefinitionDecorationGenerator;
  group: string;
  id: string;
  legacyAssetTypes: string[];
  name: string;
  repeatingPatternOptions: ProductDefinitionDecorationPatternOption;
  deprecated: boolean;
  designTemplates: DesignDecorationLocationDesignTemplate[];
  tagOptions: TagOption[];
  disabled: boolean;
}

export type Texture = {
  name: string;
  color: string;
  hex: string;
  textWhite?: boolean;
  textureUrl: string;
  hidden?: boolean;
};

export type TagOption = {
  name: string;
  shape: string;
  ratio: number;
  widthInInches: number;
  heightInInches: number;
  moldCode: string;
  borderX: number;
  borderY: number;
  textures: Texture[];
  stitchThicknessInches?: number;
};

export interface DecorationImage {
  assetType: string;
  fullSizeUrl: string;
  originalUrl: string;
  assetName: string;
  url: string;
  quality: string;
  useColorExtractionAndMatching: boolean;
  selectedColors?: {
    chartColor: AssetColor;
    transparent: boolean;
  }[];
}

export interface ProductDefinitionDecorationDisplay {
  listOrder: number;
  colorImgUrl: string;
  hideSizeValue: boolean;
}

export interface ProductDefinitionDecorationPatternOption {
  dpi: number;
  logo: ProductDefinitionDecorationPatternOptionLogo;
  output: { width: number; height: number };
  patterns: ProductDefinitionDecorationPatternOptionPattern[];
}

export interface ProductDefinitionDecorationPatternOptionLogo {
  minWidthInches: number;
  maxWidthInches: number;
  minHeightInches: number;
  maxHeightInches: number;
  increment: number;
}

export interface ProductDefinitionDecorationPatternOptionPattern {
  horizontalOffset: ProductDefinitionDecorationPatternOptionPatternType;
  horizontalSpacing: ProductDefinitionDecorationPatternOptionPatternType;
  name: string;
  verticalOffset: ProductDefinitionDecorationPatternOptionPatternType;
  verticalSpacing: ProductDefinitionDecorationPatternOptionPatternType;
}

export interface ProductDefinitionDecorationPatternOptionPatternType {
  minWidthInches: number;
  maxWidthInches: number;
  minHeightInches: number;
  maxHeightInches: number;
  increment: number;
}

export interface ProductDefinitionFabric {
  colors: ProductDefinitionColor[];
  defaultColor: ProductDefinitionColor;
  description: string;
  factory: string;
  group: string;
  name: string;
}

export interface ProductDefinitionColor {
  chartName: string;
  colorCompatibility: { colorTendsToBeBledOn: boolean };
  column: string;
  fallback: string;
  generatorHex: string;
  hex: string;
  label: string;
  name: string;
  techpackColorName: string;
  textureUrl: string;
  disabled: boolean;
  hidden: boolean;
}

export interface ProductDefinitionGenerator {
  sides: ProductDefinitionGeneratorSide[];
}

export interface ProductDefinitionGeneratorSide {
  alwaysRedraw: boolean;
  highlightOverlayFactor: number;
  highlightTests: string;
  highlightUrl: string;
  imageUrl: string;
  name: string;
  zIndex: number;
  zIndexHighlight: number;
}

export interface ProductDefinitionLabel {
  color: ProductDefinitionColor[];
  description: string;
  position: string;
  type: string;
}

export interface ProductDefinitionPart {
  elements: ProductDefinitionPartElement[];
  inheritFrom: string;
  mainElement: string;
  name: string;
  required: boolean;
}

export interface ProductDefinitionPartElement {
  defaultStyle: string;
  display: ProductDefinitionPartElementDisplay;
  fabrics: ProductDefinitionFabric[];
  inherit: boolean;
  name: string;
  required: boolean;
  styles: ProductDefinitionPartElementStyle[];
}

export interface ProductDefinitionPartElementDisplay {
  menuIconUrl: string;
  displayMethod: "colorPicker" | "colorPalette" | "icon" | "none";
  displayable: boolean;
  group?: string;
  backgroundColor?: string;
}

export interface ProductDefinitionPartElementStyle {
  credits: number;
  defaultLayer: string;
  disablingWhenSelected: [];
  display: ProductDefinitionPartElementDisplay;
  layers: ProductDefinitionPartElementStyleLayer[];
  name: string;
  pattern: string;
  positionIds: string[];
  disabled: boolean;
  useColorCombo: any; ///FIXME - find the right type
  colorCombos?: PartElementStyleColorCombo[];
}

export interface PartElementStyleColorCombo {
  colors: string[];
  label: string;
  name: string;
}

export interface ProductDefinitionPartElementStyleLayer {
  credits: number;
  display: ProductDefinitionPartElementDisplay;
  excludeFromColorCount: boolean;
  fabricNames: string[];
  generator: ProductDefinitionGenerator;
  layerLabel: string;
  name: string;
  required: boolean;
  excludeFromInheritance: boolean;
  techpack: { type: string; position: string };
}

export interface ProductDefinitionPositionGroup {
  mutuallyExclusiveGroupNames: string[];
  name: string;
  unavailableWith: { elementName: string; partName: string; styleName: string };
}

export interface ProductDefinitionPosition {
  allowedDecorationTypes: string[];
  designState: ProductDefinitionPositionDesignState;
  generator: ProductDefinitionPositionGenerator;
  id: string;
  techpack: ProductDefinitionPositionTechpack;
}

export interface ProductDefinitionPositionDesignState {
  dropdownLabel: string;
  groupName: string;
  increment: number;
  maxHeightInches: number;
  maxWidthInches: number;
  minHeightInches: number;
  minWidthInches: number;
  positionName: string;
}

export interface ProductDefinitionPositionGenerator {
  boxHeightInches: number;
  boxWidthInches: number;
  sides: ProductDefinitionPositionGeneratorSize[];
}

export interface ProductDefinitionPositionGeneratorSize {
  h: number;
  name: string;
  w: number;
  x: number;
  y: number;
  zIndex: number;
}

export interface ProductDefinitionPositionTechpack {
  alignmentX: string;
  alignmentY: string;
  assetFilenamePrefix: string;
  conditionalPattern: ProductDefinitionPositionTechpackPattern[];
  patternUrl: string;
  position: string;
}

export interface ProductDefinitionPositionTechpackPattern {
  heightGreaterThan: string;
  patternUrl: string;
}

// Cart
export interface CartError {
  ids: string[];
}

export interface CartItemError {
  id: string;
}

export interface SizeRecommendation {
  percentage: number;
  size: string;
}

export interface ItemPricing {
  cartItemId: string;
  factoryId: FactoryId;
  factory?: string; // Factory name/code (optional - only for ordered items)
  factoryPosition?: number; // Position within factory group (1-based)
  totalInFactory?: number; // Total items in same factory
  factoryInfo?: string; // Formatted factory info e.g., "RI 1/3"
  selectedShippingTier: ShippingTier;
  hangTags: boolean;
  individualPolyBags: boolean;
  shippingOptions: ShippingOption[];
  shippingOptionsCost: {
    tier: string;
    shippingTotal: number;
  }[];
  sizeRecommendations?: SizeRecommendation[];
  designId: string;
  designName: string;
  projectId: string;
  teamId: string;
  productUrls: string[];
  productName: string;
  projectName: string;
  quantity: number;
  sizes: Size[];
  pricePerUnit: number;
  productTotal: number; // quantity * pricePerUnit + extraSizeTotal. Shipping excluded
  shippingPerUnit: number;
  shippingTotal: number;
  numberOfExtraSize: number;
  extraSizeTotal: number; // numberOfExtraSize * pricePerExtraSize
  pricePerExtraSize: number;
  carbonNeutralityFeeTotal: number;
  carbonNeutral: boolean;
  itemPriceBreakdown: {
    basePrice: number;
    extraCredits: number;
    numberOfCredits: number;
    freeCredits: number;
    priceForExtraCredits: number;
    pricePerExtraCredits: number;
  };
  distributor?: {
    extraSizeTotal: number;
    numberOfExtraSize: number;
    pricePerExtraSize: number;
    pricePerUnit: number;
    productTotal: number;
    shippingPerUnit: number;
    shippingTotal: number;
    carbonNeutralityFeeTotal: number;
    itemPriceBreakdown: {
      basePrice: number;
      extraCredits: number;
      numberOfCredits: number;
      freeCredits: number;
      priceForExtraCredits: number;
      pricePerExtraCredits: number;
    };
  };
  creditDetails?: CreditDetails[];
  minimumQuantityPerSizeReached: boolean;
  deprecatedDesign: boolean;
  containsColorsEndOfLife: boolean;
  productVersionNeedUpgrade: boolean;
  quantityRequirements: {
    minimumQuantityPerSize: number;
  };
}

export interface ComboItems {
  currency: Currency;
  quantity: number;
  productSubtotal: number;
  shippingSubtotal: number;
  familyName: string;
  items: ItemPricing[];
  aboveMinimumQuantity: boolean;
  belowMaximumQuantity: boolean;
  quantityRequirements: ComboItemQuantityRequirements;
  extraSizeChargeDetails: ExtraSizeChargeDetails[];
  distributor?: {
    productSubtotal: number;
    shippingSubtotal: number;
    carbonNeutralityFeeTotal: number;
  };
}

export interface ComboItemQuantityRequirements {
  minimumQuantity: number;
  maximumQuantity: number;
}

export interface ShippingOption {
  id: string;
  tier: ShippingTier;
  hideDelayAfter: number;
  priceBrackets: {
    orderBelow: number;
    price: { [key in Currency]: number };
  }[];
  minWeeks: number;
  maxWeeks: number;
}

export interface Cart {
  currency: Currency;
  comboItems: ComboItems[];
  shippingTotal: number;
  productsTotal: number;
  orderTotal: number;
  pricingRestrictionsAreMet: boolean;
  allItemsAreBelowMaximumQuantity: boolean;
  carbonNeutral: boolean;
  carbonNeutralityFeeTotal: number;
  distributor?: {
    shippingTotal: number;
    productsTotal: number;
    //includes shippingTotal
    orderTotal: number;
    carbonNeutralityFeeTotal: number;
  };
  quoteRequest?: boolean;
}

export interface CartItem {
  deletedAt?: Date;
  deprecatedDesign: boolean;
  designId: string;
  designName: string;
  id: string;
  orderId?: string;
  orderItemDetails?: string;
  pricing: CartItemPricing;
  productImages: Image[];
  productName: string;
  projectId: string;
  projectName: string;
  sizes: Size[];
  teamId: string;
  teamName: string;
  versionName: string;
  bundle?: CartItem[];
  designState: {
    // TODO TO remove once the backend is give me creditsUsedInDesign in Cart.Pricing.creditsUsedInDesign
    customizationPricing: DesignPricing;
  };
}

export interface CartItemPricing {
  error: CartItemError;
  qty: number;
  minQty: number;
  minQtyPerSize: number;
  hasMinimum: boolean;
  currency: Currency;
  extraCredits: number;
  extraPricePerSize: ExtraPricePerSize[];
  freeCredits: number;
  hasMinimumPerSize: boolean;
  hasReachedMaximum: boolean;
  extraSizeChargeDetails: ExtraSizeChargeDetails[];
  price: number;
  priceOfExtraCreditsPerUnit: number;
  priceOfExtraSizes: number;
  priceOfOneExtraCredit: number;
  pricePerUnit: number;
  priceWithoutExtraSizesNorShipping: number;
  priceWithoutShipping: number;
  priceOfShippingPerUnit: number;
}

export interface ExtraPricePerSize {
  price: number;
  priceOfOneExtraSize: number;
  qty: number;
  sizeName: string;
}

export interface ExtraSizeChargeDetails {
  extraSizePrice: number;
  fromSizeName: string;
  maxQty: number;
  minQty: number;
  toSizeName: string;
}

//Mockup

export interface UserLogo {
  logoId: string;
  url: string;
  size: number;
  lastModified: string;
}

export interface CreateMockupForm {
  //to remove once we have tenant implemented
  logoType: "logo" | "logomark" | "custom" | "jameo" | "attraction" | "none";
  logo: Image | string;
  logoId?: string;
  customLogo?: string;
  customLogoId?: string;
  marketType: "recreo" | "promo";
  withIntroPage: boolean;
  withOurMagicTouchPage: boolean;
  withProductsPage: boolean;
  withDetailsProductPage: boolean;
  withSimpleProductPage: boolean;
  withClosingPage: boolean;
  projectName: string;
  graphicDesigner: string;
  distributorName?: string;
  language: Language;
  currency: Currency;
  includePrices: boolean;
  includeDistributorPrices: boolean;
  includeAllPrices: boolean;
  includeCustomLabels: boolean;
  designs: {
    designId: string;
    designName: string;
    proposalNumber: string;
    images: (string | Image)[];
  }[];
}

//EDITOR

export interface EditorSelection {
  selectedDesignId?: string;
  selectedPart: string;
  selectedElementName: string;
  selectedIndex: string;
}

export interface DesignStateType {
  designDecorationsFromDesignState: DesignDecoration[] | undefined;
  designFromDesignState: Design | undefined;
  images: Image[] | undefined;
  productLine?: ProductLineType;
  shipping: ShippingOption[] | undefined;
  designWarning: boolean;
  migrationNeeded: boolean;
  colorWarning: boolean;
  productVersionNeedUpgrade: boolean;
  containsColorsEndOfLife: boolean;
  deprecatedDesignItems: DeprecatedDesignItem[];
  colorBleedingItems: ColorBleedingItem[];
  colorEndOfLifeItems: ColorEndOfLifeItem[];
  isProductDefLoading: boolean;
  setIsProductDefLoading: (isLoading: boolean) => void;
  hasLoadingFailed: boolean;
  loadingFailedErrorKey: Id | undefined;
  isUpdating: boolean;
  setIsUpdating: (isUpdating: boolean) => void;
  onDesignChange: DebouncedFunc<
    (
      action: DESIGN_CHANGE_ENUM,
      data: any,
      skipDebounce?: boolean
    ) => Promise<void>
  >;
  onAssetTypeChange: ({
    decorationId,
    assetId,
    assetType,
    name,
  }: {
    decorationId: string;
    assetId: string;
    assetType: string;
    name: string;
  }) => Promise<void | {
    errorKey: any;
  }>;
  partsDefinition: ProductDefinitionPart[];
  availableDecorationsDefinition: ProductDefinitionDecoration[];
  designValueOfSelectedElement: DesignPartElement | undefined;
  selectedPartElementDef: ProductDefinitionPartElement | undefined;
  designPricing: DesignPricing | undefined;
  deliveryTime: DeliveryTime | undefined;
  siblingProducts: DesignStateSiblingProduct[] | undefined;
  sizesOption: Size[] | undefined;
  onSelectionChange: (
    partName: string,
    attributeName: string,
    index?: string
  ) => void;
  updateDecorationPreviewBackgroundColor: (
    decorationId: string,
    previewBackgroundColor: GLASS_MAGNIFIER_BACKGROUND_COLORS
  ) => void;
  selection: EditorSelection;
  isPartsDefinitionLoading: boolean;
  productDefinition: ProductDefinition | undefined;
  specRevisionId?: string;
}

export interface DeprecatedDesignItem {
  type: string;
  partName: string;
  elementName: string;
  styleName: string;
  layerName?: string;
}

export interface ColorBleedingItem {
  bleeding?: boolean;
  bledOn?: boolean;
  partName: string;
  elementName: string;
  styleName: string;
  layerName: string;
}

export interface ColorEndOfLifeItem {
  partName: string;
  elementName: string;
  styleName: string;
  layerName: string;
}

/// DISTRIBUTORS

export type DistributorRoleAction = "promote" | "deny" | "revoke";

export type DistributorStatus = "active" | "pending" | "inactive" | "denied";

export type Distributor = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: DistributorStatus;
  createdAt: Date;
};

/// PRICING

export interface PriceRange {
  qty: number;
  price: number;
  shipping: number;
  distributorOnly?: boolean;
}

export interface CreditDetails {
  credits: number;
  displayLabels: string[];
  explanation: string;
  fabric: string;
  id: string;
  name: string;
  type: string;
}

export interface ExtraSizeCharge {
  sizeName: string;
  sizeFreeWithOrderAbove: number;
}

export interface DesignPriceBrackets {
  baseProductPriceBrackets: PriceRange[];
  unitPriceBrackets: PriceRange[];
  freeCredits: number;
  creditsUsed: number;
  extraCredits: number;
  priceOfExtraCredits: number; // already computed (qty * pricePerExtraCredit)
  pricePerExtraCredit: number;
  creditsDetails: CreditDetails[];
  minQty: number;
  minQtyPerSize?: number;
  priceOfExtraSize: number;
  carbonNeutralityFee: number;
  chargeExtraSize: ExtraSizeCharge[];
  shippingOptions: ShippingTierForCurrency[];
  distributor?: DesignPriceBracketsDistributorPrices;
}

export interface SippingPriceBracketForCurrency {
  orderBelow: number;
  price: number;
}

export interface ShippingTierForCurrency {
  tier: string;
  minWeeks: number;
  maxWeeks: number;
  priceBrackets: SippingPriceBracketForCurrency[];
  hideDelayAfter: number;
}

export interface DesignPriceBracketsDistributorPrices {
  regularUserMaxOrderSize: number;
  distributorMaxOrderSize: number;
  msrp: PriceRange[];
  minimumSellingPrice: PriceRange[];
  minimumSellingPriceMargin: number;
  distributorCost: PriceRange[];
  distributorMargin: number;
}

//AssetManager

export interface AssetColor {
  name: string;
  hex: string;
  label: string;
  techpackColorName: string;
  chartName: string;
  disabled: boolean;
  migrateTo?: string;
}

export interface ProjectArtwork {
  id: string;
  assetId: string;
  assetName: string;
  projectId: string;
  projectName: string;
  createdAt: string;
  updatedAt: string;
  url: string;
}

export interface GroupedAssetToProject {
  [key: string]: ProjectArtwork[];
}

///

export interface Tenant {
  tenantName: string;
  logo: {
    logo: string;
    logomark: string;
  };
  tenantId: "default" | string;
}

/// TEMPLATES

export interface Palette {
  id: number;
  name: string;
  default?: boolean;
  description: string;
  colors: PaletteColors;
}

export interface PaletteColors {
  main: {
    name: string;
    hex: string;
  };
  secondary: {
    name: string;
    hex: string;
  };
  accent: {
    name: string;
    hex: string;
  };
  backup?: {
    name: string;
    hex: string;
  };
}
